import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import axios from 'axios';

// v2(CZ): 행마다 is_call(통화 유형 여부)이 붙었다. 옛 캐시에는 없어 '부재' 선택이 6시간 동안 안 보이므로 키를 바꿨다.
const CLIENT_TYPE_CACHE_KEY = 'wn.clientTypeCache.v2';
const CLIENT_TYPE_TTL_MS = 6 * 60 * 60 * 1000;

/**
 * 로그인 상태의 보관소.
 *
 * 기본은 `sessionStorage` 다 — **탭 단위**라 탭을 닫으면 그 세션이 끝난다. 사무실 공용 PC 에서
 * 다음 사람이 앞사람 계정으로 들어가지 않게 하는 장치라 일반 직원에게는 그대로 둔다.
 *
 * 다만 그 성질 때문에 **어드민(master)이 고객 링크를 새 탭에서 열면 로그인이 없는 방문자**가
 * 된다. Weekly 개발중 게이트가 켜진 동안 그 화면은 '개발중입니다!' 로 보인다 — 같은 계정 같은
 * 링크인데 탭만 다른 것이고, 검수할 방법이 없다(2026-09-16 실측·사용자 지시 "admin 계정 상대로는
 * 다 열어"). 그래서 **master 계정에 한해** 같은 브라우저의 다른 탭으로 세션을 이어 준다.
 *
 * 범위를 master 로 좁힌 이유: 자격증명을 localStorage 에 두면 브라우저를 닫아도 남는다(토큰 자체의
 * 24시간 만료까지). 전 직원에게 적용하면 공용 PC 의 위험이 그만큼 커지므로, 실제로 막혀서 일을
 * 못 하는 다섯 개 남짓의 master 계정에만 적용한다. 로그아웃·강등하면 그 자리에서 지운다.
 */
const SESSION_KEY = 'vuexStore';
/**
 * 공유를 끊은 시각(localStorage, 브라우저 전체)과 이 탭의 공유 세션이 시작된 시각
 * (sessionStorage, 탭 단위). 둘을 비교해 **로그아웃보다 먼저 시작된 탭은 공유본을 다시
 * 만들지 못하게** 한다. 아래 setItem 주석 참고.
 */
const SHARE_REVOKED_KEY = 'wn.share.revokedAt';
const SHARE_SINCE_KEY = 'wn.share.since';

function isMasterSnapshot(raw) {
	try {
		const s = JSON.parse(raw);
		return !!(s && s.userInfo && s.userInfo.mem_type === 'master'
			&& s.token && s.token.accessToken);
	} catch (e) {
		return false;
	}
}

function revokedAt() {
	try { return Number(window.localStorage.getItem(SHARE_REVOKED_KEY)) || 0; } catch (e) { return 0; }
}
function shareSince() {
	try { return Number(window.sessionStorage.getItem(SHARE_SINCE_KEY)) || 0; } catch (e) { return 0; }
}
function stampSince(t) {
	try { window.sessionStorage.setItem(SHARE_SINCE_KEY, String(t)); } catch (e) { /* 탭 단위 유지만 실패 */ }
}

const sessionShare = {
	getItem(key) {
		// **세션 키가 아니면 손대지 않는다.** vuex-persistedstate 는 초기화 때 저장소가 살아
		// 있는지 더미 키로 쓰고 지우는 시험을 한다(실측: setItem 뒤 removeItem). 그것까지
		// '로그아웃' 으로 해석하면 아래 공유 표식이 페이지를 열 때마다 지워져, 로그아웃 뒤에도
		// 남아 있는 탭이 공유본을 다시 만들어 버린다.
		if (key !== SESSION_KEY) {
			try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
		}
		try {
			const own = window.sessionStorage.getItem(key);
			if (own) return own;
		} catch (e) { /* 사생활 보호 모드 등, 아래로 내려간다 */ }
		try {
			// 새 탭에는 sessionStorage 가 비어 있다. master 로 저장된 세션만 이어받는다.
			const shared = window.localStorage.getItem(key);
			if (shared && isMasterSnapshot(shared)) {
				try { window.sessionStorage.setItem(key, shared); } catch (e) { /* 이어받기만 실패 */ }
				// 이어받은 시각을 찍어 둔다. 나중에 어느 탭에서 로그아웃하면 이 값이 그보다
				// 앞서므로 이 탭은 공유본을 다시 만들지 못한다.
				stampSince(Date.now());
				return shared;
			}
		} catch (e) { /* 저장소를 못 읽으면 로그인 화면으로 간다 */ }
		return null;
	},
	setItem(key, value) {
		try { window.sessionStorage.setItem(key, value); } catch (e) { /* 저장 실패가 화면을 막지 않는다 */ }
		if (key !== SESSION_KEY) return;
		try {
			// master 가 아니게 되는 순간(로그아웃·강등·다른 계정 로그인) 공유본을 지운다.
			if (!isMasterSnapshot(value)) {
				const had = window.localStorage.getItem(key);
				window.localStorage.removeItem(key);
				try { window.sessionStorage.removeItem(SHARE_SINCE_KEY); } catch (e) { /* ignore */ }
				// **공유를 끊은 시각을 남긴다.** 이게 없으면 한 탭에서 로그아웃해도 다른 탭이
				// 자기 sessionStorage 에 살아 있는 master 스냅샷을 다음 저장 때 그대로 다시
				// 써 넣고, 그 뒤에 연 새 탭이 그것을 이어받는다(Codex 리뷰가 실제로 재현했다).
				// 로그아웃한 사람 입장에서는 로그아웃이 되지 않은 것이다.
				if (had) window.localStorage.setItem(SHARE_REVOKED_KEY, String(Date.now()));
				return;
			}
			// 이 탭의 공유 세션이 마지막 로그아웃보다 먼저 시작됐으면 공유본을 만들지 않는다.
			// (그 탭 자신은 자기 sessionStorage 로 계속 일한다. 예전부터 탭 단위 세션의 성질이고
			//  이번 공유가 바꾸는 부분이 아니다.)
			let since = shareSince();
			if (!since) { since = Date.now(); stampSince(since); }
			if (since < revokedAt()) return;
			window.localStorage.setItem(key, value);
		} catch (e) { /* 용량 초과·사생활 보호 모드면 탭 단위로만 유지된다 */ }
	},
	removeItem(key) {
		try { window.sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
		if (key !== SESSION_KEY) return;
		try { window.sessionStorage.removeItem(SHARE_SINCE_KEY); } catch (e) { /* ignore */ }
		try {
			if (window.localStorage.getItem(key)) {
				window.localStorage.setItem(SHARE_REVOKED_KEY, String(Date.now()));
			}
			window.localStorage.removeItem(key);
		} catch (e) { /* ignore */ }
	},
};

export default createStore({
	state: {
		token: { accessToken: '', refreshToken: '' },
		userInfo: { mem_id: null, mem_name: null, mem_type: null, mem_uid: null, client_view: 'N', rent_client_view: 'N' },
		guest: false,
		institution: null,
		// CM: Weekly '개발중' 게이트가 서버에서 켜져 있는가. null = 아직 물어보지 않음.
		// 서버(WEEKLY_DEV_ONLY)가 진실이고 화면은 그 값을 따라간다 — 이게 없으면 서버에서
		// 게이트를 꺼도 메뉴·버튼·라우터 가드가 계속 막힌 채로 남는다.
		weeklyGate: null,
		// DE: 이 직원이 Weekly 를 쓸 수 있는가(서버 /admin/api/report/access). null = 아직 모름(모르면 막는다).
		// 기본은 팀장 이상(팀장·이사·대표)과 관리자, 관리자가 시스템관리 > 팀 관리에서 직원마다 부여·회수한다. 게이트처럼 저장하지 않는다.
		weeklyAccess: null,
		postST: false,
		pageList: [],
		pageCnt: 1,
		clientTypeList: [],
	},
	mutations: {
		setToken(state, data) {
			state.token = data;
		},
		setUserInfo(state, data) {
			// 다른 계정으로 로그인하면 앞 계정의 Weekly 권한을 들고 있지 않는다(새로 물어볼 때까지 막는다).
			// 로그인은 신원을 먼저 비우고(mem_uid null) 헤더가 나중에 채운다. 빈 신원에서 채워지는 것은 계정이 바뀐 것이
			// 아니다: 그때 지우면 로그인 직후 먼저 도착한 권한 답이 사라져 다음 화면 이동까지 메뉴가 숨는다(리뷰 지적).
			const prev = state.userInfo.mem_uid;
			if (data.mem_uid == null || (prev != null && Number(data.mem_uid) !== Number(prev))) state.weeklyAccess = null;
			state.userInfo.mem_id = data.mem_id;
			state.userInfo.mem_name = data.mem_name;
			state.userInfo.mem_type = data.mem_type;
			state.userInfo.mem_uid = data.mem_uid;
			state.userInfo.client_view = data.client_view;
			state.userInfo.rent_client_view = data.rent_client_view;
		},
		setPostST(state, data) {
			state.postST = data;
		},
		setPage(state, data) {
			state.pageList[data.idx] = data.idList;
			// 상세창을 열 때마다 결과 ID 목록(수천 건)이 새 칸에 쌓이는데 지우는 곳이 없었다. 이 상태가
			// 그대로 sessionStorage(vuexStore)에 저장되고 window.open 으로 여는 모든 탭에 복사되므로,
			// 하루 종일 쓴 탭은 수 MB 까지 자라 저장소 한도(5MB)에 닿는다. 상세창의 이전·다음 이동은
			// 자기 칸(pageIdx) 하나만 읽고, 새 탭은 열리는 순간의 복사본을 쓰므로 오래된 칸은 지워도 된다.
			// 최근 30칸만 남긴다(빈 칸은 JSON 에서 null 한 단어라 크기가 무시할 만하다).
			const keep = 30;
			for (let i = 0; i < data.idx - keep; i++) {
				if (state.pageList[i] !== undefined) state.pageList[i] = undefined;
			}
		},
		setGuest(state, data) {
			state.guest = data;
		},
		setInstitution(state, data) {
			state.institution = data || null;
		},
		setClientTypeList(state, list) {
			state.clientTypeList = Array.isArray(list) ? list : [];
		},
		setWeeklyGate(state, on) {
			state.weeklyGate = !!on;
		},
		setWeeklyAccess(state, v) {
			state.weeklyAccess = v === null ? null : !!v;
		},
	},
	actions: {
		// eslint-disable-next-line no-unused-vars
		callSetToken({ state, commit }, data) {
			commit('setToken', data);
		},

		// eslint-disable-next-line no-unused-vars
		callSetUserInfo({ state, commit }, data) {
			commit('setUserInfo', data);
		},

		// eslint-disable-next-line no-unused-vars
		callSetPostST({ state, commit }, data) {
			commit('setPostST', data);
		},

		// eslint-disable-next-line no-unused-vars
		callSetPage({ state, commit }, data) {
			commit('setPage', data);
		},

		// eslint-disable-next-line no-unused-vars
		callSetGuest({ state, commit }, data) {
			commit('setGuest', data);
		},

		// eslint-disable-next-line no-unused-vars
		callSetInstitution({ state, commit }, data) {
			commit('setInstitution', data);
		},

		// P1b: clienttype 캐싱 — 4 view 가 매번 fan-out 으로 호출하던 동일 endpoint.
		// fetcher 인자로 component 의 $apiGET 을 받아 (axios + auth interceptor 재사용)
		// circular import 피함. 6h TTL localStorage + in-memory state hydrate 순서:
		//   1) state.clientTypeList 가 채워져 있으면 즉시 반환
		//   2) localStorage 의 expiry 유효한 cache 로 hydrate
		//   3) fetcher 로 fresh fetch → state + localStorage 갱신
		async ensureClientTypeList({ state, commit }, fetcher) {
			if (state.clientTypeList.length) return state.clientTypeList;

			try {
				const raw = localStorage.getItem(CLIENT_TYPE_CACHE_KEY);
				if (raw) {
					const parsed = JSON.parse(raw);
					if (parsed && Date.now() < parsed.expiry && Array.isArray(parsed.list) && parsed.list.length) {
						commit('setClientTypeList', parsed.list);
						return parsed.list;
					}
				}
			} catch (_) {
				// corrupted cache → fall through to fetch
			}

			const list = typeof fetcher === 'function' ? await fetcher() : null;
			if (Array.isArray(list) && list.length) {
				commit('setClientTypeList', list);
				try {
					localStorage.setItem(CLIENT_TYPE_CACHE_KEY, JSON.stringify({
						list,
						expiry: Date.now() + CLIENT_TYPE_TTL_MS,
					}));
				} catch (_) {
					// quota / private mode → in-memory only
				}
				return list;
			}
			return [];
		},

		/**
		 * Weekly 게이트 상태를 서버에 물어본다(로그인 불필요, 응답은 on:true/false 하나뿐).
		 * 못 물어보면 **막힌 것으로 본다** — 모르는 상태에서 여는 쪽으로 무너지면 안 된다.
		 * 캐시하지 않는다: 이 값은 운영에서 사람이 바꾸는 스위치라, 페이지를 새로 열면 다시 본다.
		 */
		async fetchWeeklyGate({ state, commit }) {
			const base = process.env.VUE_APP_HOST_BACK || '';
			// 사용 권한(DE)은 로그인한 직원에 대한 것이라 토큰을 붙여 묻는다. 알림을 띄우는 $apiGET 을 쓰지 않는다 —
			// 화면 이동마다 도는 요청이라 실패해도 조용해야 한다.
			const access = async () => {
				const at = state.token && state.token.accessToken;
				if (!at) return commit('setWeeklyAccess', null);
				// 제휴 게스트는 묻지 않는다: 늘 막혀 있고(getWeeklyBlocked), 서버도 403 이라 화면 이동마다 감사로그에
				// '권한 없는 접근 시도' 가 쌓인다(리뷰 지적).
				if (!!state.guest && !!state.institution) return commit('setWeeklyAccess', false);
				// 답은 물을 때의 토큰에 대한 것이다. 그사이 로그아웃·다른 계정 로그인·토큰 갱신이 있었으면 버린다
				// (앞 계정의 답을 새 계정에 얹지 않는다. 갱신된 토큰으로는 다음 화면 이동 때 다시 묻는다).
				const same = () => !!state.token && state.token.accessToken === at;
				try {
					const re = await axios.get(`${base}/admin/api/report/access`, { timeout: 8000, headers: { authorization: at } });
					// 토큰 만료(200 + status 402)는 답이 아니다. 다음 화면 이동 때 갱신된 토큰으로 다시 묻는다(그때까지 앞 값 유지).
					if (re.data && re.data.status == 402) return undefined;
					if (!same()) return undefined;
					return commit('setWeeklyAccess', !!(re.data && re.data.allowed));
				} catch (e) {
					// 403 은 '못 쓴다' 다. 네트워크 오류는 앞 값을 둔다(메뉴가 깜빡이지 않게).
					if (e.response && e.response.status === 403 && same()) commit('setWeeklyAccess', false);
					return undefined;
				}
			};
			const gate = async () => {
				try {
					const re = await axios.get(`${base}/pub/api/report/gate`, { timeout: 8000 });
					commit('setWeeklyGate', !!(re.data && re.data.on));
				} catch (e) {
					commit('setWeeklyGate', true);
				}
			};
			await Promise.all([gate(), access()]);
		},

		// eslint-disable-next-line no-unused-vars
		clearClientTypeCache({ commit }) {
			commit('setClientTypeList', []);
			try {
				localStorage.removeItem(CLIENT_TYPE_CACHE_KEY);
			} catch (_) {
				// ignore
			}
		},
	},
	getters: {
		getToken(state) {
			if (state.token.accessToken && state.token.refreshToken) {
				return { accessToken: state.token.accessToken, refreshToken: state.token.refreshToken };
			} else {
				return false;
			}
		},

		getUserInfo(state) {
			if (state.userInfo.mem_id) {
				return state.userInfo;
			} else {
				return { mem_id: null, mem_name: null, mem_type: null, mem_uid: null, client_view: 'N', rent_client_view: 'N' };
			}
		},

		getPostST(state) {
			return state.postST;
		},

		getPageList(state) {
			return state.pageList;
		},

		getGuest(state) {
			return state.guest;
		},

		getInstitution(state) {
			return state.institution;
		},

		// 제휴기관(은행/기업) 게스트 = guest 이면서 institution 태그 보유. 일반 게스트(내부 직원 데모,
		// institution 없음)와 구분 — 직원 전용 화면(매매고객/매매작업/시스템관리) 노출/접근 제한 대상 식별용.
		getPartnerGuest(state) {
			return !!state.guest && !!state.institution;
		},

		/**
		 * Weekly 를 이 사람에게 막아야 하는가. 화면 네 곳(헤더 메뉴·매매검색 'Weekly 담기'·물건자료 'Weekly 저장'·
		 * 라우터 가드)이 같은 답을 쓰도록 여기 한 곳에 둔다. 서버도 같은 기준으로 403 을 준다.
		 *   · 제휴기관 게스트                  → 언제나 차단(사내 영업자료)
		 *   · 관리자(master)                   → 통과
		 *   · 개발중 게이트가 켜졌거나 모른다  → 차단(모르면 막는 쪽)
		 *   · 그 밖                            → 사용 권한이 있다고 서버가 답했을 때만 통과(DE: 기본은 팀장)
		 */
		getWeeklyBlocked(state) {
			if (!!state.guest && !!state.institution) return true;
			if (state.userInfo.mem_type === 'master') return false;
			if (state.weeklyGate !== false) return true;
			return state.weeklyAccess !== true;
		},
		/** 막힌 이유. 라우터 가드의 안내문이 쓴다. 'unknown' 은 권한을 아직 못 물어본 것이다(네트워크 오류 등). */
		getWeeklyBlockReason(state) {
			if (state.userInfo.mem_type !== 'master' && state.weeklyGate !== false) return 'dev';
			if (!(state.guest && state.institution) && state.weeklyAccess == null) return 'unknown';
			return 'access';
		},
	},
	modules: {},
	plugins: [createPersistedState({
		key: 'vuexStore',
		storage: sessionShare,
		// weeklyGate·weeklyAccess 는 저장하지 않는다. 저장해 두면 서버에서 게이트를 끄거나 권한을 바꾼 뒤에도
		// **이미 열어 둔 탭**이 옛 값에 갇힌다(Codex 리뷰). 나머지 상태는 그대로 유지.
		reducer: (state) => {
			const { weeklyGate, weeklyAccess, ...rest } = state;   // eslint-disable-line no-unused-vars
			return rest;
		},
	})],
});
